// Universal Race Components for Tutorials
// Provides reusable race win/loss patterns that can be assigned to any tutorial

import { WinConditionConfig } from './TutorialWinConditions';

/**
 * Configuration for a universal race tutorial
 */
export interface RaceConfig {
  /** The piece type that's racing (e.g., 'Bear', 'Crane', 'Snake') */
  pieceType: string;
  /** Row that light pieces must reach to win (default: 7) */
  lightWinRow?: number;
  /** Row that dark pieces reach to cause loss (default: 0) */
  darkLossRow?: number;
  /** Turn limit for secret win (optional) */
  secretWinTurnLimit?: number;
  /** Turn limit for overall tutorial (optional) */
  overallTurnLimit?: number;
  /** Custom descriptions (optional - will use defaults if not provided) */
  descriptions?: {
    regular?: string;
    secret?: string;
    loss?: string;
  };
}

/**
 * Create a standard race tutorial configuration
 * Light piece races to top row (row 7), dark piece racing to bottom (row 0) causes loss
 */
export function createRaceToTop(config: RaceConfig): WinConditionConfig {
  const lightWinRow = config.lightWinRow ?? 7;
  const darkLossRow = config.darkLossRow ?? 0;
  
  const winCondition: WinConditionConfig = {
    regularWin: { 
      type: 'reach_row', 
      target: config.pieceType, 
      targetSide: 'light', 
      row: lightWinRow 
    },
    lossConditions: [
      { type: 'piece_captured', target: config.pieceType, targetSide: 'light' },
      { type: 'enemy_reaches_row', target: config.pieceType, targetSide: 'dark', row: darkLossRow }
    ],
    descriptions: {
      regular: config.descriptions?.regular ?? `Race your ${config.pieceType.toLowerCase()} to row ${lightWinRow}`,
      secret: config.descriptions?.secret ?? `Race your ${config.pieceType.toLowerCase()} to row ${lightWinRow}`,
      loss: config.descriptions?.loss ?? `Better luck next time!`
    }
  };

  // Add secret win with turn limit if specified
  if (config.secretWinTurnLimit) {
    winCondition.secretWin = {
      type: 'reach_row', 
      target: config.pieceType, 
      targetSide: 'light', 
      row: lightWinRow,
      turnLimit: config.secretWinTurnLimit
    };
    winCondition.descriptions.secret = config.descriptions?.secret ?? 
      `Race your ${config.pieceType.toLowerCase()} to row ${lightWinRow} by turn ${config.secretWinTurnLimit}`;
  }

  // Add overall turn limit if specified
  if (config.overallTurnLimit) {
    winCondition.lossConditions!.push({ type: 'turn_limit', turnLimit: config.overallTurnLimit });
  }

  return winCondition;
}

/**
 * Create a dual-direction race tutorial configuration
 * Light piece can win by reaching either end of the board (row 0 OR row 7)
 */
export function createDualDirectionRace(config: RaceConfig): WinConditionConfig {
  const darkLossRow = config.darkLossRow ?? 0;

  const winCondition: WinConditionConfig = {
    regularWin: {
      type: 'multiple',
      conditions: [
        { type: 'reach_row', target: config.pieceType, targetSide: 'light', row: 0, turnLimit: config.overallTurnLimit },
        { type: 'reach_row', target: config.pieceType, targetSide: 'light', row: 7, turnLimit: config.overallTurnLimit }
      ]
    },
    lossConditions: [
      { type: 'piece_captured', target: config.pieceType, targetSide: 'light' },
      { type: 'enemy_reaches_row', target: config.pieceType, targetSide: 'dark', row: darkLossRow }
    ],
    descriptions: {
      regular: config.descriptions?.regular ?? `Race your ${config.pieceType.toLowerCase()} to either end of the board (row 0 or row 7)`,
      secret: config.descriptions?.secret ?? `Capture the dark ${config.pieceType.toLowerCase()} to achieve secret victory!`,
      loss: config.descriptions?.loss ?? `Better luck next time!`
    }
  };

  // Add secret win with capture if no turn limit specified
  if (!config.secretWinTurnLimit) {
    winCondition.secretWin = {
      type: 'capture_target',
      target: config.pieceType,
      targetSide: 'dark'
    };
  } else {
    // Add secret win with turn limit for dual direction
    winCondition.secretWin = {
      type: 'multiple',
      conditions: [
        { type: 'reach_row', target: config.pieceType, targetSide: 'light', row: 0, turnLimit: config.secretWinTurnLimit },
        { type: 'reach_row', target: config.pieceType, targetSide: 'light', row: 7, turnLimit: config.secretWinTurnLimit }
      ]
    };
    winCondition.descriptions.secret = config.descriptions?.secret ??
      `Race your ${config.pieceType.toLowerCase()} to either end by turn ${config.secretWinTurnLimit}`;
  }

  // Add overall turn limit if specified
  if (config.overallTurnLimit) {
    winCondition.lossConditions!.push({ type: 'turn_limit', turnLimit: config.overallTurnLimit });
  }

  return winCondition;
}

/**
 * Create a capture-focused race tutorial configuration
 * Primary goal is capturing, secondary goal is racing
 * config.pieceType refers to the PLAYER'S piece (light), target is the enemy piece to capture
 */
export function createCaptureRace(config: RaceConfig & { targetPiece?: string }): WinConditionConfig {
  const lightWinRow = config.lightWinRow ?? 7;
  const darkLossRow = config.darkLossRow ?? 0;
  const targetPiece = config.targetPiece ?? config.pieceType; // Default: target same piece type
  
  const winCondition: WinConditionConfig = {
    secretWin: { 
      type: 'capture_target', 
      target: targetPiece, 
      targetSide: 'dark' 
    },
    regularWin: { 
      type: 'capture_target', 
      target: targetPiece, 
      targetSide: 'dark' 
    },
    lossConditions: [
      { type: 'piece_captured', target: config.pieceType, targetSide: 'light' },
      { type: 'enemy_reaches_row', target: targetPiece, targetSide: 'dark', row: darkLossRow }
    ],
    descriptions: {
      regular: config.descriptions?.regular ?? `Capture the dark ${targetPiece.toLowerCase()}`,
      secret: config.descriptions?.secret ?? `Capture the dark ${targetPiece.toLowerCase()} to achieve secret victory!`,
      loss: config.descriptions?.loss ?? `Better luck next time!`
    }
  };

  // Add turn limits if specified
  if (config.secretWinTurnLimit && config.overallTurnLimit) {
    // Secret win: capture by turn limit
    winCondition.secretWin = {
      type: 'capture_target', 
      target: targetPiece, 
      targetSide: 'dark',
      turnLimit: config.secretWinTurnLimit
    };
    
    // Regular win: capture with longer turn limit  
    winCondition.regularWin = {
      type: 'capture_target', 
      target: targetPiece, 
      targetSide: 'dark',
      turnLimit: config.overallTurnLimit
    };
    
    winCondition.lossConditions!.push({ type: 'turn_limit', turnLimit: config.overallTurnLimit });
    
    winCondition.descriptions.secret = `Capture the dark ${targetPiece.toLowerCase()} by turn ${config.secretWinTurnLimit}`;
    winCondition.descriptions.regular = `Capture the dark ${targetPiece.toLowerCase()} by turn ${config.overallTurnLimit}`;
  }

  return winCondition;
}

// Pre-configured common race patterns
export const COMMON_RACE_PATTERNS = {
  /** Standard race to row 7, secret win by turn 4, overall limit turn 6 */
  STANDARD_RACE: (pieceType: string) => createRaceToTop({
    pieceType,
    secretWinTurnLimit: 4,
    overallTurnLimit: 6
  }),

  /** Dual-direction race (row 0 or 7), capture for secret win */
  DUAL_RACE: (pieceType: string) => createDualDirectionRace({
    pieceType
  }),

  /** Dual-direction race with turn limits - race by 5 for secret, 7 total moves */
  DUAL_RACE_TIMED: (pieceType: string) => createDualDirectionRace({
    pieceType,
    secretWinTurnLimit: 5,
    overallTurnLimit: 7,
    descriptions: {
      regular: `Race your ${pieceType.toLowerCase()} to either end of the board (row 0 or row 7)`,
      secret: `Race your ${pieceType.toLowerCase()} to either end by turn 5!`,
      loss: `Better luck next time!`
    }
  }),

  /** Capture-focused with race backup, capture by turn 6, race by turn 11 */
  CAPTURE_RACE: (targetPieceType: string) => createCaptureRace({
    pieceType: 'Bear', // Player controls Bear in Bear Tutorial 3
    targetPiece: targetPieceType, // Target to capture (e.g., 'Crane')
    secretWinTurnLimit: 6,
    overallTurnLimit: 11
  }),

  /** Simple race to row 7, no time limits */
  SIMPLE_RACE: (pieceType: string) => createRaceToTop({
    pieceType
  })
};

// Example usage:
// 'Bear': {
//   1: COMMON_RACE_PATTERNS.STANDARD_RACE('Bear'),
//   2: COMMON_RACE_PATTERNS.CAPTURE_RACE('Bear'),
// },
// 'Crane': {
//   0: COMMON_RACE_PATTERNS.DUAL_RACE('Crane'),
// },
// 'Snake': {
//   0: COMMON_RACE_PATTERNS.SIMPLE_RACE('Snake'),
//   1: createRaceToTop({ pieceType: 'Snake', lightWinRow: 6, darkLossRow: 1 }) // Custom
// }